'use client'

import { logger } from '@/lib/logger'
import { create } from 'zustand'
import { persist, subscribeWithSelector } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

// Types for authentication
export interface AuthUser {
  userId: string
  username: string
  email?: string
  role: UserRole
  tenantId?: number
  schoolIds?: number[]
  departmentIds?: number[]
  categoryIds?: number[]
  impersonatingUserId?: number | null
  permissions?: string[]
  isAuthenticated: boolean
}

export enum UserRole {
  SuperAdmin = 1, // Super Admin
  SchoolCorpAdmin = 2, // School Corp Admin
  SchoolAdmin = 3, // School Admin
  DepartmentAdmin = 4, // Department Admin
  Purchaser = 7, // Purchaser (was incorrectly 5)
  SchoolCorpAdminReadOnly = 8, // School Corp Admin - Read Only (was incorrectly 6)
  SchoolAdminReadOnly = 9, // School Admin - Read Only (was incorrectly 7)
  DepartmentAdminReadOnly = 10, // Department Admin - Read Only (was incorrectly 8)
}

export interface AuthState {
  user: AuthUser | null
  token: string | null
  isLoading: boolean
  error: string | null
  returnUrl: string | null
}

export interface AuthActions {
  // Auth actions
  setUser: (user: AuthUser) => void
  setToken: (token: string) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setReturnUrl: (url: string | null) => void

  // Session management
  loadSession: () => void
  saveSession: (user: AuthUser, token: string) => void
  clearSession: () => void

  // Helper methods
  isAuthenticated: () => boolean
  hasRole: (role: UserRole) => boolean
  getRoleName: () => string
  getRedirectUrl: () => string
}

export type AuthStore = AuthState & AuthActions

const initialState: AuthState = {
  user: null,
  token: null,
  isLoading: false,
  error: null,
  returnUrl: null,
}

// Helper to get role name
const getRoleNameById = (role: UserRole): string => {
  const roleNames: Record<UserRole, string> = {
    [UserRole.SuperAdmin]: 'Super Admin',
    [UserRole.SchoolCorpAdmin]: 'School Corp Admin',
    [UserRole.SchoolAdmin]: 'School Admin',
    [UserRole.DepartmentAdmin]: 'Department Admin',
    [UserRole.Purchaser]: 'Purchaser',
    [UserRole.SchoolCorpAdminReadOnly]: 'School Corp Admin - Read Only',
    [UserRole.SchoolAdminReadOnly]: 'School Admin - Read Only',
    [UserRole.DepartmentAdminReadOnly]: 'Department Admin - Read Only',
  }
  return roleNames[role] || 'Unknown'
}

// Helper to get redirect URL based on role (matching legacy behavior)
const getRedirectUrlByRole = (role: UserRole): string => {
  const redirectMap: Record<UserRole, string> = {
    [UserRole.SuperAdmin]: '/pages/system/contacts.html',
    [UserRole.SchoolCorpAdmin]: '/pages/system/school-corp-view.html',
    [UserRole.SchoolCorpAdminReadOnly]: '/pages/system/school-corp-view.html',
    [UserRole.SchoolAdmin]: '/pages/system/school-view.html',
    [UserRole.SchoolAdminReadOnly]: '/pages/system/school-view.html',
    [UserRole.DepartmentAdmin]: '/pages/system/school-dept-view.html',
    [UserRole.DepartmentAdminReadOnly]: '/pages/system/school-dept-view.html',
    [UserRole.Purchaser]: '/pages/public/account.html',
  }
  return redirectMap[role] || '/pages/public/account.html'
}

export const useAuthSessionStore = create<AuthStore>()(
  persist(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,

        // Basic setters
        setUser: (user: AuthUser) => {
          set((state) => {
            state.user = user
          })
        },

        setToken: (token: string) => {
          set((state) => {
            state.token = token
          })
        },

        setLoading: (loading: boolean) => {
          set((state) => {
            state.isLoading = loading
          })
        },

        setError: (error: string | null) => {
          set((state) => {
            state.error = error
          })
        },

        setReturnUrl: (url: string | null) => {
          set((state) => {
            state.returnUrl = url
          })
        },

        // Session management
        loadSession: () => {
          const state = get()
          if (state.user && state.token) {
            logger.info('Session loaded from store', { userId: state.user.userId })
          }
        },

        saveSession: (user: AuthUser, token: string) => {
          set((state) => {
            state.user = user
            state.token = token
            state.error = null
          })

          // Save to sessionStorage for legacy compatibility (matching exact keys from legacy system)
          if (typeof window !== 'undefined') {
            try {
              sessionStorage.setItem('myId', user.userId)
              sessionStorage.setItem('mySchoolCorpId', String(user.tenantId || 0))
              sessionStorage.setItem('mySchoolId', String(user.schoolIds?.[0] || 0))
              sessionStorage.setItem('myDeptId', String(user.departmentIds?.[0] || 0))
              sessionStorage.setItem('myCatId', String(user.categoryIds?.[0] || 0))

              // Role name as string (legacy expects single role name)
              const roleName = getRoleNameById(user.role)
              sessionStorage.setItem('myRoles', roleName)
              sessionStorage.setItem('roles', roleName)

              // Permissions as comma-separated string
              sessionStorage.setItem('myPermissions', user.permissions?.join(',') || '')

              // Legacy flags
              sessionStorage.setItem('isImpersonating', user.impersonatingUserId ? 'true' : 'false')

              // Clear any kiosk state
              localStorage.removeItem('activeKiosk')

              logger.info('Session saved', { userId: user.userId, role: user.role, roleName })
            } catch (error) {
              logger.error('Failed to save session to sessionStorage', error)
            }
          }
        },

        clearSession: () => {
          set((state) => {
            state.user = null
            state.token = null
            state.error = null
            state.returnUrl = null
          })

          // Clear sessionStorage
          if (typeof window !== 'undefined') {
            try {
              sessionStorage.clear()
              localStorage.removeItem('activeKiosk')
            } catch (error) {
              logger.error('Failed to clear sessionStorage', error)
            }
          }
        },

        // Helper methods
        isAuthenticated: () => {
          const state = get()
          return !!(state.user?.isAuthenticated && state.token)
        },

        hasRole: (role: UserRole) => {
          const state = get()
          return state.user?.role === role
        },

        getRoleName: () => {
          const state = get()
          return state.user ? getRoleNameById(state.user.role) : 'Unknown'
        },

        getRedirectUrl: () => {
          const state = get()

          // If we have a return URL, use it
          if (state.returnUrl && state.returnUrl !== '0' && state.returnUrl !== 'undefined') {
            return state.returnUrl
          }

          // Check localStorage for last page
          if (typeof window !== 'undefined') {
            const lastPage = localStorage.getItem('lastPage')
            if (lastPage && lastPage !== '0' && lastPage !== 'undefined') {
              return lastPage
            }
          }

          // Default to role-based redirect
          return state.user ? getRedirectUrlByRole(state.user.role) : '/account'
        },
      })),
    ),
    {
      name: 'auth-session-store',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
      }),
    },
  ),
)
